// src/pages/VenueProfile.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  Divider,
  Paper,
  Avatar,
  Skeleton,
  Tooltip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import DateTimeField from "../components/DateTimeField";
import { validateEventDates } from "../services/eventDates";
import MultiTagField from "../components/MultiTagField";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AvailabilityList from "../components/AvailabilityList";
import ShareIcon from "@mui/icons-material/Share";
import StadiumIcon from "@mui/icons-material/Stadium";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import PlaceIcon from "@mui/icons-material/Place";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PaidIcon from "@mui/icons-material/Paid";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { gigService, profileService, venueService } from "../services/resources.service";
import { canEditEvent, canInviteArtistsToVenue, hasPermission, PERMISSIONS } from "../services/authRoles";
import { managesPage } from "../services/profilePageSetup";
import { buildRecipientInboxPath } from "../services/messaging";
import { canContactVenue } from "../services/proFeatures";
import DiscoveryMap from "../components/DiscoveryMap";
import { formatDate, formatDateTime, formatMoneyRange, getMessage, useLocale } from "../i18n/locale";
import FollowButton from "../components/FollowButton";
import ProfileRating from "../components/ProfileRating";
import ProFeatureControl from "../components/ProFeatureControl";
import EventReviewDialog from "../components/EventReviewDialog";
import ProfileGallery from "../components/ProfileGallery";
import LinkedAccountBadge from "../components/LinkedAccountBadge";
import { BANNER_IMAGE_FALLBACK, getBannerImage, getProfileImage, imageFallbackHandler, PROFILE_IMAGE_FALLBACK } from "../services/profileImages";

async function fetchVenueById(id) {
  return venueService.get(id);
}

async function fetchVenueEvents(id) {
  return venueService.events(id);
}

async function fetchVenueReviews(id) {
  return venueService.reviews(id);
}

async function fetchSimilarVenues(id) {
  return venueService.similar(id);
}

// ---------------- Small UI helpers ----------------
function CapacityChip({ cap }) {
  if (cap === null || cap === undefined || cap === "" || !Number.isFinite(Number(cap)) || Number(cap) <= 0) {
    return null;
  }

  return (
    <Chip
      size="small"
      icon={<PeopleAltIcon />}
      label={`${cap} cap`}
      variant="outlined"
    />
  );
}
function BudgetChip({ min, max, currency }) {
  const localeContext = useLocale();
  const hasMin = min !== null && min !== undefined && min !== "" && Number.isFinite(Number(min));
  const hasMax = max !== null && max !== undefined && max !== "" && Number.isFinite(Number(max));

  if (!hasMin && !hasMax) return null;

  return (
    <Chip
      size="small"
      icon={<PaidIcon />}
      label={formatMoneyRange(min, max, { locale: localeContext.locale, currency: currency || localeContext.currency })}
      variant="outlined"
    />
  );
}
function FacilityBadge({ kind, on }) {
  const map = {
    pa: { label: "In-house PA", icon: <VolumeUpIcon /> },
    lights: { label: "Lights", icon: <EmojiObjectsIcon /> },
    backline: { label: "Backline", icon: <PrecisionManufacturingIcon /> },
    accessible: { label: "Accessible", icon: <AccessibilityNewIcon /> },
  };
  const m = map[kind];
  if (!m) return null;
  return (
    <Chip
      size="small"
      icon={m.icon}
      label={m.label}
      variant={on ? "filled" : "outlined"}
      color={on ? "success" : "default"}
    />
  );
}

function EventMiniCard({ evt, onOpen, onEdit, isPast = false }) {
  const localeContext = useLocale();

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: isPast ? "action.hover" : "background.paper" }}>
      <CardMedia
        component="img"
        height="110"
        image={evt.poster || BANNER_IMAGE_FALLBACK}
        onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
        alt={evt.title}
        loading="lazy"
        sx={{ filter: isPast ? "saturate(.65)" : "none" }}
      />
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" color="text.secondary">
            {formatDateTime(evt.startAt, localeContext)}
          </Typography>
          {isPast && <Chip size="small" label={getMessage(localeContext.locale, "venueEventCompleted")} />}
        </Stack>
        <Typography variant="subtitle1" fontWeight={800} noWrap>
          {evt.title}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          {evt.tags?.map((t) => (
            <Chip key={t} label={t} size="small" />
          ))}
        </Stack>
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" onClick={() => onOpen(evt.id)}>
            {getMessage(localeContext.locale, "managedPageOpen")}
          </Button>
          {onEdit && (
            <Button fullWidth variant="contained" onClick={() => onEdit(evt.id)} startIcon={<EditIcon />}>
              {getMessage(localeContext.locale, "eventEdit")}
            </Button>
          )}
        </Stack>
      </Box>
    </Card>
  );
}

function SimilarVenueCard({ v, onOpen }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardMedia component="img" height="110" image={v.photo || BANNER_IMAGE_FALLBACK} onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)} alt={v.name} loading="lazy" />
      <CardContent>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle1" fontWeight={800} noWrap>{v.name}</Typography>
          <LinkedAccountBadge ownerUserId={v.ownerUserId} />
        </Stack>
        <Typography variant="body2" color="text.secondary" noWrap>
          {v.city}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          <CapacityChip cap={v.capacity} />
          <Chip size="small" icon={<StarRoundedIcon />} label={v.rating} variant="outlined" />
        </Stack>
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button fullWidth variant="outlined" onClick={() => onOpen(v.id)}>
          View venue
        </Button>
      </Box>
    </Card>
  );
}

// Gig post dialog (MVP)
function GigPostDialog({ open, venue, onClose, onSubmit, submitting = false, error = "" }) {
  const locale = useLocale();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [genres, setGenres] = useState(venue?.genresHosted?.slice(0, 2).join(", ") || "");
  const [budgetMin, setBudgetMin] = useState(venue?.budgetMin ?? 1000);
  const [budgetMax, setBudgetMax] = useState(venue?.budgetMax ?? 3000);
  const [backline, setBackline] = useState(venue?.facilities?.backline ? "Basic drum kit & 2x amps" : "");
  const [paymentTerms, setPaymentTerms] = useState("EFT within 48h");
  const [deadline, setDeadline] = useState("");
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    if (open) {
      setTitle("");
      setDate("");
      setGenres(venue?.genresHosted?.slice(0, 2).join(", ") || "");
      setBudgetMin(venue?.budgetMin ?? 1000);
      setBudgetMax(venue?.budgetMax ?? 3000);
      setBackline(venue?.facilities?.backline ? "Basic drum kit & 2x amps" : "");
      setPaymentTerms("EFT within 48h");
      setDeadline("");
      setDateError("");
    }
  }, [open, venue]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>Post a gig at “{venue?.name}”</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {(dateError || error) && <Alert severity="error">{dateError || error}</Alert>}
          <TextField label="Title" placeholder="Friday Indie Night" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <DateTimeField label="Show date & time" type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} fullWidth />
            <DateTimeField label="Application deadline" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} fullWidth slotProps={{ htmlInput: { max: date ? date.slice(0, 10) : undefined } }} />
          </Stack>
          <MultiTagField label="Genres" value={genres} onChange={(e) => setGenres(e.target.value)} fullWidth />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField label="Budget min (R)" type="number" value={budgetMin} onChange={(e) => setBudgetMin(Number(e.target.value))} fullWidth />
            <TextField label="Budget max (R)" type="number" value={budgetMax} onChange={(e) => setBudgetMax(Number(e.target.value))} fullWidth />
          </Stack>
          <TextField label="Backline provided" placeholder="e.g., Drum kit, 2x guitar amps, 3x vocal mics" value={backline} onChange={(e) => setBackline(e.target.value)} fullWidth />
          <TextField label="Payment terms" placeholder="Cash on the night / EFT within 48h" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disabled={submitting || !title.trim() || !date || !deadline}
          onClick={() => {
            const validationKey = validateEventDates({ startAt: date, applicationDeadline: deadline });
            if (validationKey) {
              setDateError(getMessage(locale.locale, validationKey));
              return;
            }
            setDateError("");
            onSubmit({
              title,
              date,
              genres: genres.split(",").map((s) => s.trim()).filter(Boolean),
              budgetMin,
              budgetMax,
              backline,
              paymentTerms,
              appDeadline: deadline,
            });
          }}
        >
          Publish gig
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------- Page ----------------
export default function VenueProfile({ onRequireAuth, user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locale, currency } = useLocale();
  const userId = user?.id;
  const userRole = user?.role;
  const mayContactByRole = !user || hasPermission(user, PERMISSIONS.CONTACT_VENUE);
  const contactLocked = Boolean(user) && mayContactByRole && !canContactVenue(user);

  const [venue, setVenue] = useState(null);
  const [events, setEvents] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [similar, setSimilar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [canInviteArtists, setCanInviteArtists] = useState(false);
  const [tab, setTab] = useState(0); // 0 Overview, 1 Media, 2 Events, 3 Reviews
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const [gigOpen, setGigOpen] = useState(false);
  const [gigSubmitting, setGigSubmitting] = useState(false);
  const [gigError, setGigError] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [v, e, r, s] = await Promise.all([
        fetchVenueById(id),
        fetchVenueEvents(id),
        fetchVenueReviews(id),
        fetchSimilarVenues(id),
      ]);
      if (!active) return;
      setVenue(v);
      setEvents(e);
      setReviews(r);
      setSimilar(s);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    setIsOwner(false);
    setCanInviteArtists(false);
    if (userRole !== "VENUE") return () => { active = false; };

    profileService.me()
      .then((profile) => {
        if (active) {
          setIsOwner(managesPage(profile, "venuePages", id));
          setCanInviteArtists(canInviteArtistsToVenue({ role: userRole }, profile, id));
        }
      })
      .catch(() => {
        if (active) {
          setIsOwner(false);
          setCanInviteArtists(false);
        }
      });

    return () => { active = false; };
  }, [id, userId, userRole]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: venue?.name ?? "Venue", url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      setSnack({ open: true, msg: "Link copied to clipboard", severity: "info" });
    }
  };

  const budgetText = useMemo(() => {
    if (!venue) return "";
    return formatMoneyRange(venue.budgetMin, venue.budgetMax, { locale, currency: venue.currency || currency });
    // typical booking budget range
  }, [currency, locale, venue]);
  const pastEvents = useMemo(() => events?.past ? [...events.past].reverse() : [], [events]);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar
          sx={{
            gap: 1,
            width: "100%",
            maxWidth: 1248,
            mx: "auto",
            px: { xs: 1.5, sm: 3 },
          }}
        >
          <IconButton edge="start" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Venue
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            {isOwner && (
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/venues/${id}/edit`)}
              >
                {getMessage(locale, "artistProfileEdit")}
              </Button>
            )}
            <FollowButton
              subjectType="venue"
              subjectId={id}
              user={user}
              redirectTo={`/venues/${id}`}
              onRequireAuth={onRequireAuth}
              onFeedback={(msg, severity) => setSnack({ open: true, msg, severity })}
              followerCount={venue?.followerCount}
            />
            <Tooltip title="Share">
              <IconButton onClick={share} aria-label="share">
                <ShareIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: { xs: 0, sm: 3 }, px: { xs: 0, sm: 3 } }}>
        {/* Hero */}
        <Box sx={{ position: "relative" }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{
                height: { xs: 200, sm: 280, md: 360 },
                borderRadius: { xs: 0, sm: 3 },
              }}
            />
          ) : (
            <>
              <Box
                role="img"
                aria-label={`${venue.name} venue`}
                sx={{
                  height: { xs: 200, sm: 280, md: 360 },
                  borderRadius: { xs: 0, sm: 3 },
                  bgcolor: "secondary.main",
                  backgroundImage: `linear-gradient(180deg, rgba(29,33,40,.04), rgba(29,33,40,.44)), url(${getBannerImage(venue)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <Avatar
                src={getProfileImage(venue)}
                imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }}
                alt={venue.name}
                sx={{
                  width: { xs: 88, sm: 112 },
                  height: { xs: 88, sm: 112 },
                  position: "absolute",
                  left: { xs: 16, sm: 32 },
                  bottom: { xs: -44, sm: -56 },
                  border: (theme) => `4px solid ${theme.palette.background.default}`,
                  boxShadow: "0 8px 24px rgba(29,33,40,.18)",
                }}
              >
                <StadiumIcon />
              </Avatar>
            </>
          )}
        </Box>

        <Box sx={{ pt: { xs: 6, sm: 8 }, pb: 2, px: { xs: 2, sm: 0 } }}>
        {/* Header */}
        {loading ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Skeleton width="40%" height={36} />
            <Skeleton width="30%" height={24} />
          </Stack>
        ) : (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h5" fontWeight={900}>{venue.name}</Typography>
              <LinkedAccountBadge ownerUserId={venue.ownerUserId} sx={{ fontSize: "1.35rem" }} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              <PlaceIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {venue.city} • {venue.address}
            </Typography>
            <ProfileRating rating={venue.rating} reviewCount={reviews?.length} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mt: 1 }}>
              <CapacityChip cap={venue.capacity} />
              <BudgetChip min={venue.budgetMin} max={venue.budgetMax} currency={venue.currency} />
              {venue.genresHosted.map((g) => (
                <Chip key={g} size="small" icon={<MusicNoteIcon />} label={g} />
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {venue.siteUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  onClick={() => window.open(venue.siteUrl, "_blank", "noopener,noreferrer")}
                >
                  Website
                </Button>
              )}
              {mayContactByRole && (
                <ProFeatureControl locked={contactLocked} label={getMessage(locale, "venueContact")}>
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={contactLocked}
                      onClick={() => {
                        const redirectTo = buildRecipientInboxPath("venue", venue.id);
                        if (!user) return onRequireAuth?.({ redirectTo, reasonKey: "loginModalMessageReason" });
                        navigate(redirectTo);
                      }}
                    >
                      {getMessage(locale, "venueContact")}
                    </Button>
                  </span>
                </ProFeatureControl>
              )}
              {hasPermission(user, PERMISSIONS.POST_GIG) && <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/venues/gig/new?venueId=${encodeURIComponent(venue.id)}`)}
              >
                Post a gig
              </Button>}
              {canInviteArtists && <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/artists?city=${encodeURIComponent(venue.city)}`)}
              >
                {getMessage(locale, "venueInviteArtists")}
              </Button>}
            </Stack>
          </Stack>
        )}

        {/* Tabs */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Overview" />
            <Tab label={getMessage(locale, "profileMedia")} />
            <Tab label="Events" />
            <Tab label="Reviews" />
          </Tabs>

          {/* Overview */}
          {tab === 0 && (
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  {/* Facilities */}
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                    Facilities
                  </Typography>
                  {loading ? (
                    <Skeleton variant="rounded" height={36} sx={{ borderRadius: 2 }} />
                  ) : (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <FacilityBadge kind="pa" on={venue.facilities.pa} />
                      <FacilityBadge kind="lights" on={venue.facilities.lights} />
                      <FacilityBadge kind="backline" on={venue.facilities.backline} />
                      <FacilityBadge kind="accessible" on={venue.facilities.accessible} />
                    </Stack>
                  )}

                  {/* Map */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Location
                    </Typography>
                    {loading ? (
                      <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
                    ) : (
                      <DiscoveryMap
                        items={[venue]}
                        getCoordinates={(item) => item.geo}
                        getLabel={(item) => item.name}
                        labelMessageKey="venueMapLabel"
                        height={260}
                      />
                    )}
                  </Paper>

                  {/* Policies */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Booking policy
                    </Typography>
                    {loading ? (
                      <>
                        <Skeleton height={18} />
                        <Skeleton height={18} width="85%" />
                      </>
                    ) : (
                      <Typography variant="body1">{venue.policies}</Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Sidebar */}
                <Grid size={{ xs: 12, md: 4 }}>
                  {/* At-a-glance */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      At a glance
                    </Typography>
                    {loading ? (
                      <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2 }} />
                    ) : (
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                          <CalendarMonthIcon />
                          <Typography variant="body2" color="text.secondary">
                            Typical budget: {budgetText}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                          <PeopleAltIcon />
                          <Typography variant="body2" color="text.secondary">
                            Capacity: {venue.capacity}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                          <MusicNoteIcon />
                          <Typography variant="body2" color="text.secondary">
                            Hosts: {venue.genresHosted.join(", ")}
                          </Typography>
                        </Stack>
                      </Stack>
                    )}
                    {hasPermission(user, PERMISSIONS.POST_GIG) && <Button
                      fullWidth
                      variant="contained"
                      sx={{ mt: 1.5 }}
                      onClick={() => navigate(`/venues/gig/new?venueId=${encodeURIComponent(venue.id)}`)}
                    >
                      Post a gig
                    </Button>}
                  </Paper>
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      {getMessage(locale, "availabilityTitle")}
                    </Typography>
                    {loading ? (
                      <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
                    ) : (
                      <AvailabilityList availability={venue.availability} locale={locale} timeZone={venue.timeZone} />
                    )}
                    {isOwner && (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CalendarMonthIcon />}
                        sx={{ mt: 1.5 }}
                        onClick={() => navigate(`/venues/${encodeURIComponent(venue.id)}/edit#availability`)}
                      >
                        {getMessage(locale, "venueManageAvailability")}
                      </Button>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Media */}
          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Grid container spacing={2}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                      <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : venue.photos?.length ? (
                <ProfileGallery photos={venue.photos} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {getMessage(locale, "profileNoMedia")}
                </Typography>
              )}
            </Box>
          )}

          {/* Events */}
          {tab === 2 && (
            <Box sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" fontWeight={800}>
                  {getMessage(locale, "venueUpcomingEvents")}
                </Typography>
                {isOwner && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => navigate(`/venues/gig/new?venueId=${encodeURIComponent(venue.id)}`)}
                  >
                    {getMessage(locale, "venueEventsPostGig")}
                  </Button>
                )}
              </Stack>
              {loading ? (
                <Grid container spacing={2}>
                  {Array.from({ length: 2 }).map((_, i) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={i}>
                      <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : events?.upcoming?.length ? (
                <Grid container spacing={2}>
                  {events.upcoming.map((evt) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={evt.id}>
                      <EventMiniCard
                        evt={evt}
                        onOpen={(eid) => navigate(`/events/${eid}`)}
                        onEdit={canEditEvent(user, evt) ? (eid) => navigate(`/events/${eid}/edit`) : null}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {getMessage(locale, "venueNoUpcomingEvents")}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography component="h3" variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                {getMessage(locale, "venuePastEvents")}
              </Typography>
              {loading ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                  </Grid>
                </Grid>
              ) : pastEvents.length ? (
                <Grid container spacing={2}>
                  {pastEvents.map((evt) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={evt.id}>
                      <EventMiniCard evt={evt} isPast onOpen={(eid) => navigate(`/events/${eid}`)} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {getMessage(locale, "venueNoPastEvents")}
                </Typography>
              )}
            </Box>
          )}

          {/* Reviews */}
          {tab === 3 && (
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Stack spacing={1}>
                  <Skeleton height={72} />
                  <Skeleton height={72} />
                </Stack>
              ) : reviews?.length ? (
                <List>
                  {reviews.map((r) => (
                    <React.Fragment key={r.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar>
                            <StarRoundedIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                              <Typography variant="subtitle2">{r.reviewer.name}</Typography>
                              <Chip size="small" label={`Prof ${r.ratings.professionalism}★`} />
                              <Chip size="small" label={`Punct ${r.ratings.punctuality}★`} />
                              <Chip size="small" label={`Sound ${r.ratings.sound}★`} />
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {formatDate(r.createdAt, { locale })}
                              </Typography>
                            </Stack>
                          }
                          secondary={r.text}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No reviews yet.
                </Typography>
              )}
              {hasPermission(user, PERMISSIONS.RATE_VENUE_AFTER_GIG) && <Button
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={() => setReviewOpen(true)}
              >
                {getMessage(locale, "reviewVenueTitle")}
              </Button>}
            </Box>
          )}
        </Paper>

        <EventReviewDialog
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          targetType="venue"
          targetId={id}
          onCreated={(review) => {
            setReviews((current) => [review, ...(current || [])]);
            setSnack({ open: true, msg: getMessage(locale, "reviewSuccess"), severity: "success" });
          }}
        />

        {/* Similar venues */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Similar venues
          </Typography>
          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 2 }).map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : similar?.length ? (
            <Grid container spacing={2}>
              {similar.map((v) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={v.id}>
                  <SimilarVenueCard v={v} onOpen={(vid) => navigate(`/venues/${vid}`)} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              None found.
            </Typography>
          )}
        </Paper>

        {/* Gig posting dialog */}
        <GigPostDialog
          open={gigOpen}
          venue={venue}
          submitting={gigSubmitting}
          error={gigError}
          onClose={() => {
            setGigOpen(false);
            setGigError("");
          }}
          onSubmit={async (payload) => {
            setGigSubmitting(true);
            setGigError("");
            try {
              const created = await gigService.create({
                venueId: venue.id,
                title: payload.title.trim(),
                startAt: new Date(payload.date).toISOString(),
                timezone: venue.timezone || venue.timeZone,
                city: venue.city,
                genres: payload.genres,
                budgetMinMinor: Math.round(Number(payload.budgetMin) * 100),
                budgetMaxMinor: Math.round(Number(payload.budgetMax) * 100),
                currency: venue.currency || currency,
                equipment: payload.backline.trim(),
                terms: payload.paymentTerms.trim(),
                applicationDeadline: payload.appDeadline,
              });
              const gigId = created?.gig?.id;
              const eventId = created?.event?.id;
              if (!gigId || !eventId || created.gig.eventId !== eventId) {
                throw new Error(getMessage(locale, "gigPublishIncomplete"));
              }
              setGigOpen(false);
              navigate("/events/gigs", {
                state: { publishedGigId: gigId, publishedEventId: eventId },
              });
            } catch (error) {
              setGigError(error.message || getMessage(locale, "gigPublishIncomplete"));
            } finally {
              setGigSubmitting(false);
            }
          }}
        />

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={2200}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        </Snackbar>

        <Box sx={{ height: 16 }} />
        </Box>
      </Container>
    </Box>
  );
}
